import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Store, Package, DollarSign, Check, X, AlertTriangle, Star, Mail, Send, Loader2, Download, Trash2, Wand2, Copy, UserPlus, Image as ImageIcon, ExternalLink, FileText } from "lucide-react";
import { api, imgUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-5">
      <Icon className="text-[#E8956B] mb-2" size={22} />
      <div className="text-2xl font-black">{value}</div>
      <div className="text-xs text-neutral-500 uppercase tracking-wide mt-1">{label}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [disputed, setDisputed] = useState([]);
  const [pendingInf, setPendingInf] = useState([]);
  const [news, setNews] = useState(null);
  const [savingNews, setSavingNews] = useState(false);
  const [sendingNews, setSendingNews] = useState(false);
  const [sellers, setSellers] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [inactive, setInactive] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deletingBulk, setDeletingBulk] = useState(false);

  const [campaign, setCampaign] = useState({
    subject: "Welcome to Beauty Trade Club 💄",
    cta_url: "https://beautytradeclub.com",
    cta_label: "Explore the Club",
    intro_message: "",
    recipients: "",
    template: "welcome",
  });
  const [previewHtml, setPreviewHtml] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [savedCampaigns, setSavedCampaigns] = useState([]);
  const [campaignName, setCampaignName] = useState("");
  const [editingCampaignId, setEditingCampaignId] = useState(null);

  const [leads, setLeads] = useState([]);
  const [social, setSocial] = useState({ platform: "instagram", tone: "friendly", topic: "", with_image: true });
  const [genResult, setGenResult] = useState(null);
  const [generating, setGenerating] = useState(false);

  const [blogSettings, setBlogSettings] = useState(null);
  const [blogKeywords, setBlogKeywords] = useState("");
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogGenerating, setBlogGenerating] = useState(false);

  const load = useCallback(async () => {
    const [s, p, d, pi, nl, sl, by, ina] = await Promise.all([
      api.get("/admin/stats").then((r) => r.data).catch(() => null),
      api.get("/admin/sellers/pending").then((r) => r.data).catch(() => []),
      api.get("/admin/orders/disputed").then((r) => r.data).catch(() => []),
      api.get("/admin/influencers/pending").then((r) => r.data).catch(() => []),
      api.get("/admin/newsletter").then((r) => r.data).catch(() => null),
      api.get("/admin/users/sellers").then((r) => r.data).catch(() => []),
      api.get("/admin/users/buyers").then((r) => r.data).catch(() => []),
      api.get("/admin/users/inactive").then((r) => r.data).catch(() => []),
    ]);
    setStats(s); setPending(p); setDisputed(d); setPendingInf(pi); setNews(nl); setSellers(sl); setBuyers(by);
    setInactive(ina); setSelectedIds(new Set());
    api.get("/admin/email/campaigns/saved").then((r) => setSavedCampaigns(r.data || [])).catch(() => {});
    api.get("/admin/blog/settings").then((r) => { setBlogSettings(r.data); setBlogKeywords((r.data.keywords || []).join("\n")); }).catch(() => {});
    api.get("/admin/blog").then((r) => setBlogPosts(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { navigate("/"); return; }
    load();
  }, [user, loading, load, navigate]);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/sellers/${id}/${action}`);
      toast.success(`Seller ${action}d`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This removes their account and any listings. This cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      toast.success(data.message || "User deleted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.size === inactive.length ? new Set() : new Set(inactive.map((u) => u.id))));
  };
  const bulkDelete = async () => {
    const ids = [...selectedIds];
    if (!ids.length) { toast.error("Select at least one account"); return; }
    if (!window.confirm(`Permanently delete ${ids.length} inactive account(s)? This cannot be undone.`)) return;
    setDeletingBulk(true);
    try {
      const { data } = await api.post("/admin/users/bulk-delete", { user_ids: ids });
      toast.success(`Deleted ${data.deleted} account(s)${data.skipped_admins ? ` (${data.skipped_admins} admin skipped)` : ""}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setDeletingBulk(false); }
  };

  const actInf = async (id, action) => {
    try {
      await api.post(`/admin/influencers/${id}/${action}`);
      toast.success(`Influencer ${action}d`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const resolve = async (id, action) => {
    try {
      await api.post(`/admin/orders/${id}/${action}`);
      toast.success(action === "refund" ? "Buyer refunded" : "Funds released to seller");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveNews = async () => {
    setSavingNews(true);
    try {
      await api.put("/admin/newsletter", {
        intro_message: news.intro_message, coupon_code: news.coupon_code, vip_message: news.vip_message,
      });
      toast.success("Newsletter content saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingNews(false); }
  };

  const sendNews = async () => {
    setSendingNews(true);
    try {
      const { data } = await api.post("/admin/newsletter/send-now");
      toast.success(`Newsletter sent to ${data.sent} members (${data.vip_sent} VIP)`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSendingNews(false); }
  };

  // Live preview of the outreach email (debounced)
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const t = setTimeout(() => {
      api.post("/admin/email/campaign/preview", {
        subject: campaign.subject,
        intro_message: campaign.intro_message,
        cta_url: campaign.cta_url,
        cta_label: campaign.cta_label,
        template: campaign.template,
      }).then(({ data }) => setPreviewHtml(data.html)).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [campaign.intro_message, campaign.cta_url, campaign.cta_label, campaign.subject, campaign.template, user]);

  const recipientLines = () =>
    campaign.recipients.split(/\n+/).map((s) => s.trim()).filter((s) => /@/.test(s) && /\.[a-zA-Z]{2,}/.test(s));

  const uploadRecipients = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result || "");
      // Split CSV/TXT into lines; keep only lines with an email
      const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.includes("@"));
      const merged = (campaign.recipients ? campaign.recipients + "\n" : "") + lines.join("\n");
      setCampaign((c) => ({ ...c, recipients: merged }));
      toast.success(`Added ${lines.length} recipient(s) from ${file.name}`);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const addRecipientsFrom = (list, kindLabel) => {
    const existing = new Set(
      campaign.recipients.split(/\n+/).map((s) => {
        const m = s.match(/[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/);
        return m ? m[0].toLowerCase() : "";
      }).filter(Boolean)
    );
    const newLines = [];
    (list || []).forEach((u) => {
      const email = (u.email || "").trim();
      if (!email || !email.includes("@") || existing.has(email.toLowerCase())) return;
      existing.add(email.toLowerCase());
      const name = (u.name || "").trim();
      newLines.push(name ? `${name}, ${email}` : email);
    });
    if (newLines.length === 0) { toast.info(`No new ${kindLabel} to add — they're already in your list.`); return; }
    setCampaign((c) => ({ ...c, recipients: (c.recipients ? c.recipients + "\n" : "") + newLines.join("\n") }));
    toast.success(`Added ${newLines.length} ${kindLabel} 🎉`);
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const { data } = await api.post("/admin/email/campaign/test", {
        subject: campaign.subject, intro_message: campaign.intro_message,
        cta_url: campaign.cta_url, cta_label: campaign.cta_label, template: campaign.template,
      });
      toast.success(`Test email sent to ${data.to} 📬`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSendingTest(false); }
  };

  const sendCampaign = async () => {
    const recipients = recipientLines();
    if (recipients.length === 0) { toast.error("Add at least one recipient email"); return; }
    if (!window.confirm(`Send this email to ${recipients.length} recipient(s)?`)) return;
    setSendingCampaign(true);
    try {
      const { data } = await api.post("/admin/email/campaign/send", {
        subject: campaign.subject, intro_message: campaign.intro_message,
        cta_url: campaign.cta_url, cta_label: campaign.cta_label, template: campaign.template, recipients,
      });
      toast.success(`Sent to ${data.sent}/${data.total}${data.failed.length ? ` · ${data.failed.length} failed` : ""} 🎉`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSendingCampaign(false); }
  };

  const saveCampaign = async () => {
    if (!campaignName.trim()) { toast.error("Give this campaign a name to save it"); return; }
    try {
      const { data } = await api.post("/admin/email/campaigns/save", {
        name: campaignName, subject: campaign.subject, intro_message: campaign.intro_message,
        cta_url: campaign.cta_url, cta_label: campaign.cta_label, template: campaign.template,
        recipients: campaign.recipients.split(/\n+/).map((s) => s.trim()).filter(Boolean),
        campaign_id: editingCampaignId || undefined,
      });
      setEditingCampaignId(data.id);
      toast.success(`Campaign "${data.name}" saved 💾`);
      api.get("/admin/email/campaigns/saved").then((r) => setSavedCampaigns(r.data || [])).catch(() => {});
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadCampaign = (c) => {
    setCampaign({
      subject: c.subject || "", intro_message: c.intro_message || "", cta_url: c.cta_url || "",
      cta_label: c.cta_label || "", recipients: (c.recipients || []).join("\n"), template: c.template || "welcome",
    });
    setCampaignName(c.name); setEditingCampaignId(c.id);
    toast.success(`Loaded "${c.name}"`);
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm("Delete this saved campaign?")) return;
    try {
      await api.delete(`/admin/email/campaigns/saved/${id}`);
      if (editingCampaignId === id) { setEditingCampaignId(null); setCampaignName(""); }
      setSavedCampaigns((list) => list.filter((c) => c.id !== id));
      toast.success("Campaign deleted");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveBlogSettings = async (overrides = {}) => {
    const keywords = blogKeywords.split(/\n+/).map((k) => k.trim()).filter(Boolean);
    try {
      const { data } = await api.put("/admin/blog/settings", { keywords, auto_enabled: blogSettings?.auto_enabled ?? true, ...overrides });
      setBlogSettings(data); setBlogKeywords((data.keywords || []).join("\n"));
      toast.success("Blog settings saved 💾");
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleBlogAuto = async () => {
    const next = !(blogSettings?.auto_enabled ?? true);
    await saveBlogSettings({ auto_enabled: next });
    toast.success(next ? "Daily auto-blog ON — a fresh post publishes every day 📝" : "Daily auto-blog paused");
  };

  const generateBlogNow = async () => {
    setBlogGenerating(true);
    try {
      const { data } = await api.post("/admin/blog/generate", {});
      setBlogPosts((list) => [{ ...data, content_html: undefined }, ...list]);
      toast.success(`Published: "${data.title}" 🎉`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBlogGenerating(false); }
  };

  const deleteBlogPost = async (id) => {
    if (!window.confirm("Delete this blog post?")) return;
    try {
      await api.delete(`/admin/blog/${id}`);
      setBlogPosts((list) => list.filter((p) => p.id !== id));
      toast.success("Post deleted");
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    if (user?.role === "admin") api.get("/admin/leads").then(({ data }) => setLeads(data)).catch(() => {});
  }, [user]);

  const generateSocial = async () => {
    setGenerating(true);
    setGenResult(null);
    try {
      const { data } = await api.post("/admin/social/generate", social);
      setGenResult(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenerating(false); }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied 📋`);
  };

  const downloadCSV = (rows, columns, filename) => {
    if (!rows.length) { toast.error("No records to export yet"); return; }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = columns.map((c) => esc(c.label)).join(",");
    const body = rows.map((r) => columns.map((c) => esc(r[c.key])).join(",")).join("\n");
    const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded`);
  };

  const SELLER_COLS = [
    { key: "legal_name", label: "Legal Name" }, { key: "name", label: "Display Name" },
    { key: "email", label: "Email" }, { key: "phone", label: "Phone" },
    { key: "address_line1", label: "Address 1" }, { key: "address_line2", label: "Address 2" },
    { key: "city", label: "City" }, { key: "state", label: "State" },
    { key: "zip_code", label: "ZIP" }, { key: "country", label: "Country" },
    { key: "seller_rating", label: "Rating" }, { key: "seller_review_count", label: "Reviews" },
    { key: "is_vip", label: "VIP" }, { key: "joined", label: "Joined" },
  ];
  const BUYER_COLS = [
    { key: "name", label: "Name" }, { key: "email", label: "Email" },
    { key: "shipping_address", label: "Shipping Address" }, { key: "orders", label: "Orders" },
    { key: "is_seller", label: "Also Seller" }, { key: "is_vip", label: "VIP" }, { key: "joined", label: "Joined" },
  ];
  const INACTIVE_COLS = [
    { key: "name", label: "Name" }, { key: "email", label: "Email" },
    { key: "seller_status", label: "Seller Status" }, { key: "joined", label: "Joined" },
  ];

  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <h1 className="font-display text-3xl font-bold mb-1">Admin Console</h1>
      <p className="text-neutral-500 mb-8">Verify sellers and monitor the marketplace.</p>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Stat icon={Users} label="Total Users" value={stats.total_users} />
          <Stat icon={Store} label="Verified Sellers" value={stats.total_sellers} />
          <Stat icon={Package} label="Products" value={stats.total_products} />
          <Stat icon={DollarSign} label="Platform Revenue" value={`$${stats.platform_revenue.toFixed(2)}`} />
        </div>
      )}

      <h2 className="font-display text-xl font-bold mb-4">Pending Seller Applications ({pending.length})</h2>
      <div className="space-y-3">
        {pending.length === 0 && <p className="text-neutral-400" data-testid="no-pending">No pending applications.</p>}
        {pending.map((u) => (
          <div key={u.id} data-testid={`pending-seller-${u.id}`} className="flex items-center justify-between bg-white border border-neutral-200 rounded-xl p-4">
            <div>
              <div className="font-semibold">{u.name}</div>
              <div className="text-sm text-neutral-500">{u.email}</div>
            </div>
            <div className="flex gap-2">
              <button data-testid={`approve-${u.id}`} onClick={() => act(u.id, "approve")} className="flex items-center gap-1 bg-[#E8956B] hover:bg-[#CE744A] text-white text-sm font-bold rounded-full px-4 py-2 transition-all">
                <Check size={15} /> Approve
              </button>
              <button data-testid={`reject-${u.id}`} onClick={() => act(u.id, "reject")} className="flex items-center gap-1 border border-neutral-300 text-neutral-600 hover:border-red-400 hover:text-red-400 text-sm font-bold rounded-full px-4 py-2 transition-all">
                <X size={15} /> Reject
              </button>
            </div>
          </div>
        ))}
      </div>

      <h2 className="font-display text-xl font-bold mb-4 mt-12 flex items-center gap-2">
        <Star size={18} className="text-[#E8956B]" fill="currentColor" /> Pending Influencer Applications ({pendingInf.length})
      </h2>
      <div className="space-y-3">
        {pendingInf.length === 0 && <p className="text-neutral-400" data-testid="no-pending-influencers">No pending influencer applications.</p>}
        {pendingInf.map((u) => (
          <div key={u.id} data-testid={`pending-influencer-${u.id}`} className="flex items-center justify-between bg-white border border-neutral-200 rounded-xl p-4">
            <div>
              <div className="font-semibold">{u.name}</div>
              <div className="text-sm text-neutral-500">{u.email}</div>
            </div>
            <div className="flex gap-2">
              <button data-testid={`approve-inf-${u.id}`} onClick={() => actInf(u.id, "approve")} className="flex items-center gap-1 bg-[#E8956B] hover:bg-[#CE744A] text-white text-sm font-bold rounded-full px-4 py-2 transition-all">
                <Check size={15} /> Approve
              </button>
              <button data-testid={`reject-inf-${u.id}`} onClick={() => actInf(u.id, "reject")} className="flex items-center gap-1 border border-neutral-300 text-neutral-600 hover:border-red-400 hover:text-red-400 text-sm font-bold rounded-full px-4 py-2 transition-all">
                <X size={15} /> Reject
              </button>
            </div>
          </div>
        ))}
      </div>

      <h2 className="font-display text-xl font-bold mb-4 mt-12 flex items-center gap-2">
        <AlertTriangle size={20} className="text-red-600" /> Disputed Orders ({disputed.length})
      </h2>
      <div className="space-y-3">
        {disputed.length === 0 && <p className="text-neutral-400" data-testid="no-disputes">No disputes. All good! ✨</p>}
        {disputed.map((o) => (
          <div key={o.id} data-testid={`dispute-${o.id}`} className="flex items-center gap-4 bg-white border border-red-200 rounded-xl p-4">
            <img src={imgUrl(o.product_image)} alt="" className="w-14 h-14 rounded-lg object-cover" />
            <div className="flex-1">
              <div className="font-semibold">{o.product_title}</div>
              <div className="text-sm text-neutral-500">${o.amount.toFixed(2)} · {o.buyer_name} vs {o.seller_name}</div>
              {o.dispute_reason && <div className="text-sm text-red-600 mt-1">"{o.dispute_reason}"</div>}
              {o.dispute_images?.length > 0 && (
                <div className="flex gap-2 mt-2" data-testid={`dispute-images-${o.id}`}>
                  {o.dispute_images.map((img, i) => (
                    <a key={i} href={imgUrl(img)} target="_blank" rel="noreferrer">
                      <img src={imgUrl(img)} alt="evidence" className="w-12 h-12 rounded-lg object-cover border border-neutral-200 hover:border-[#E8956B]" />
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button data-testid={`refund-${o.id}`} onClick={() => resolve(o.id, "refund")} className="text-sm font-bold border border-neutral-300 text-neutral-700 hover:border-red-400 hover:text-red-600 rounded-full px-4 py-2 transition-all">
                Refund Buyer
              </button>
              <button data-testid={`release-${o.id}`} onClick={() => resolve(o.id, "release")} className="text-sm font-bold bg-[#E8956B] hover:bg-[#CE744A] text-white rounded-full px-4 py-2 transition-all">
                Release to Seller
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* SELLER RECORDS */}
      <div className="flex items-center justify-between mb-4 mt-12">
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Store size={20} className="text-[#E8956B]" /> Seller Records ({sellers.length})
        </h2>
        <button data-testid="download-sellers-csv" onClick={() => downloadCSV(sellers, SELLER_COLS, "sellers.csv")}
          className="flex items-center gap-2 bg-[#E8956B] hover:bg-[#CE744A] text-white text-sm font-bold rounded-full px-4 py-2 transition-all">
          <Download size={15} /> Download CSV
        </button>
      </div>
      <div className="overflow-x-auto bg-white border border-neutral-200 rounded-2xl mb-4" data-testid="sellers-table">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-500 text-xs uppercase">
            <tr><th className="text-left p-3">Legal Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Phone</th><th className="text-left p-3">Address</th><th className="text-left p-3">Rating</th></tr>
          </thead>
          <tbody>
            {sellers.length === 0 && <tr><td colSpan={5} className="p-4 text-neutral-400 text-center" data-testid="no-sellers">No sellers yet.</td></tr>}
            {sellers.map((s) => (
              <tr key={s.id} className="border-t border-neutral-100">
                <td className="p-3 font-medium">{s.legal_name || s.name}</td>
                <td className="p-3">{s.email}</td>
                <td className="p-3">{s.phone || "—"}</td>
                <td className="p-3 text-neutral-500">{[s.address_line1, s.city, s.state, s.zip_code].filter(Boolean).join(", ") || "—"}</td>
                <td className="p-3">{s.seller_review_count ? `${s.seller_rating}★ (${s.seller_review_count})` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* BUYER RECORDS */}
      <div className="flex items-center justify-between mb-4 mt-10">
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Users size={20} className="text-[#E8956B]" /> Buyer Records ({buyers.length})
        </h2>
        <button data-testid="download-buyers-csv" onClick={() => downloadCSV(buyers, BUYER_COLS, "buyers.csv")}
          className="flex items-center gap-2 bg-[#E8956B] hover:bg-[#CE744A] text-white text-sm font-bold rounded-full px-4 py-2 transition-all">
          <Download size={15} /> Download CSV
        </button>
      </div>
      <div className="overflow-x-auto bg-white border border-neutral-200 rounded-2xl mb-4" data-testid="buyers-table">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-500 text-xs uppercase">
            <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Shipping Address</th><th className="text-left p-3">Orders</th></tr>
          </thead>
          <tbody>
            {buyers.length === 0 && <tr><td colSpan={4} className="p-4 text-neutral-400 text-center" data-testid="no-buyers">No buyers yet.</td></tr>}
            {buyers.map((b) => (
              <tr key={b.id} className="border-t border-neutral-100">
                <td className="p-3 font-medium">{b.name}</td>
                <td className="p-3">{b.email}</td>
                <td className="p-3 text-neutral-500">{b.shipping_address || "—"}</td>
                <td className="p-3">{b.orders}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* INACTIVE ACCOUNT CLEANUP */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 mt-10">
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Trash2 size={20} className="text-[#E8956B]" /> Inactive Accounts ({inactive.length})
        </h2>
        <div className="flex items-center gap-2">
          <button data-testid="download-inactive-csv" onClick={() => downloadCSV(inactive, INACTIVE_COLS, "inactive-accounts.csv")}
            className="flex items-center gap-2 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 text-sm font-bold rounded-full px-4 py-2 transition-all">
            <Download size={15} /> Export CSV
          </button>
          <button data-testid="bulk-delete-btn" onClick={bulkDelete} disabled={deletingBulk || selectedIds.size === 0}
            className="flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold rounded-full px-4 py-2 transition-all">
            {deletingBulk ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Delete Selected ({selectedIds.size})
          </button>
        </div>
      </div>
      <p className="text-xs text-neutral-500 mb-3">Accounts with no orders, no listings, not VIP and not an approved influencer. Safe to remove old or spam sign-ups.</p>
      <div className="overflow-x-auto bg-white border border-neutral-200 rounded-2xl mb-4" data-testid="inactive-table">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-500 text-xs uppercase">
            <tr>
              <th className="p-3 w-10">
                <input type="checkbox" data-testid="select-all-inactive" aria-label="Select all inactive"
                  checked={inactive.length > 0 && selectedIds.size === inactive.length} onChange={toggleSelectAll} />
              </th>
              <th className="text-left p-3">Name</th><th className="text-left p-3">Email</th>
              <th className="text-left p-3">Seller Status</th><th className="text-left p-3">Joined</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {inactive.length === 0 && <tr><td colSpan={6} className="p-4 text-neutral-400 text-center" data-testid="no-inactive">No inactive accounts. 🎉</td></tr>}
            {inactive.map((u) => (
              <tr key={u.id} className="border-t border-neutral-100">
                <td className="p-3">
                  <input type="checkbox" data-testid={`select-inactive-${u.id}`}
                    checked={selectedIds.has(u.id)} onChange={() => toggleSelect(u.id)} />
                </td>
                <td className="p-3 font-medium">{u.name}</td>
                <td className="p-3">{u.email}</td>
                <td className="p-3 text-neutral-500 capitalize">{u.seller_status === "none" ? "—" : u.seller_status}</td>
                <td className="p-3 text-neutral-500">{u.joined ? new Date(u.joined).toLocaleDateString() : "—"}</td>
                <td className="p-3 text-right">
                  <button data-testid={`delete-inactive-${u.id}`} onClick={() => deleteUser(u)}
                    className="inline-flex items-center gap-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all">
                    <Trash2 size={14} /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="font-display text-xl font-bold mb-4 mt-12 flex items-center gap-2">
        <Mail size={20} className="text-[#E8956B]" /> Monthly Newsletter
      </h2>
      {news && (
        <div data-testid="newsletter-panel" className="bg-white border border-neutral-200 rounded-2xl p-6 space-y-5">
          <div className="flex flex-wrap gap-6 text-sm">
            <div><span className="text-neutral-500">Subscribers: </span><b>{news.subscriber_count}</b></div>
            <div><span className="text-neutral-500">VIP members: </span><b>{news.vip_count}</b></div>
            <div><span className="text-neutral-500">Last sent: </span><b>{news.last_sent_month || "—"}</b></div>
          </div>
          <p className="text-xs text-neutral-500 -mt-2">
            Sent automatically at the start of each month to everyone who signs up. VIP members also receive a separate news &amp; bonuses email.
          </p>

          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Monthly message (to everyone)</label>
            <Textarea
              data-testid="newsletter-intro-input"
              value={news.intro_message}
              onChange={(e) => setNews({ ...news, intro_message: e.target.value })}
              className="bg-neutral-50 border-neutral-300 mt-2 min-h-24"
              placeholder="Write the message that goes to all members this month..."
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Discount code (shown to everyone)</label>
            <Input
              data-testid="newsletter-coupon-input"
              value={news.coupon_code}
              onChange={(e) => setNews({ ...news, coupon_code: e.target.value })}
              className="bg-neutral-50 border-neutral-300 mt-2 h-11 max-w-xs"
              placeholder="e.g. BEAUTY10"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">VIP-only message (news &amp; bonuses)</label>
            <Textarea
              data-testid="newsletter-vip-input"
              value={news.vip_message}
              onChange={(e) => setNews({ ...news, vip_message: e.target.value })}
              className="bg-neutral-50 border-neutral-300 mt-2 min-h-24"
              placeholder="Exclusive news and bonuses just for VIP members..."
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <button data-testid="save-newsletter-btn" onClick={saveNews} disabled={savingNews}
              className="bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-6 py-2.5 transition-all disabled:opacity-50">
              {savingNews ? "Saving..." : "Save Content"}
            </button>
            <button data-testid="send-newsletter-btn" onClick={sendNews} disabled={sendingNews}
              className="flex items-center gap-2 border-2 border-[#E8956B] text-[#E8956B] hover:bg-[#E8956B] hover:text-white font-bold rounded-full px-6 py-2.5 transition-all disabled:opacity-50">
              {sendingNews ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : <><Send size={16} /> Send Now</>}
            </button>
          </div>
        </div>
      )}

      {/* AUTO SEO BLOG */}
      <h2 className="font-display text-xl font-bold mb-4 mt-12 flex items-center gap-2">
        <FileText size={20} className="text-[#E8956B]" /> Auto SEO Blog
      </h2>
      <div data-testid="blog-panel" className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-neutral-200 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-neutral-900">Daily auto-publishing</p>
              <p className="text-xs text-neutral-500">A fresh AI-written post goes live every day, rotating through your keywords.</p>
            </div>
            <button
              data-testid="toggle-blog-auto"
              onClick={toggleBlogAuto}
              role="switch"
              aria-checked={!!blogSettings?.auto_enabled}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${blogSettings?.auto_enabled ? "bg-[#E8956B]" : "bg-neutral-300"}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${blogSettings?.auto_enabled ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">SEO keywords / topics</label>
            <p className="text-xs text-neutral-400 mt-1 mb-2">One keyword or topic per line. The AI rotates through them — one new article per day.</p>
            <Textarea
              data-testid="blog-keywords-input"
              value={blogKeywords}
              onChange={(e) => setBlogKeywords(e.target.value)}
              placeholder={"how to sell used makeup online\nis it safe to buy pre-owned beauty\nbest way to declutter your makeup collection"}
              className="bg-neutral-50 border-neutral-300 min-h-36 font-mono text-sm"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="save-blog-settings-btn" onClick={() => saveBlogSettings()}
              className="bg-neutral-900 hover:bg-neutral-800 text-white text-sm font-bold rounded-full px-5 py-2.5 transition-all">
              Save keywords
            </button>
            <button data-testid="generate-blog-btn" onClick={generateBlogNow} disabled={blogGenerating}
              className="bg-[#E8956B] hover:bg-[#CE744A] text-white text-sm font-bold rounded-full px-5 py-2.5 transition-all disabled:opacity-50 inline-flex items-center gap-2">
              {blogGenerating ? <><Loader2 size={15} className="animate-spin" /> Writing & illustrating…</> : "✨ Generate a post now"}
            </button>
          </div>
          <p className="text-[11px] text-neutral-400">Generating a post takes ~20–40s (AI writes the article and creates a matching header image).</p>
        </div>

        <div className="bg-white border border-neutral-200 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-3">
            <p className="font-bold text-neutral-900">Published posts <span className="text-neutral-400 font-normal">({blogPosts.length})</span></p>
            <a href="/blog" target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#CE744A] hover:underline inline-flex items-center gap-1"><ExternalLink size={12} /> View blog</a>
          </div>
          {blogPosts.length === 0 ? (
            <p className="text-sm text-neutral-400 py-6 text-center" data-testid="blog-no-posts">No posts yet. Add keywords and hit "Generate a post now" to publish your first one.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="blog-post-list">
              {blogPosts.map((p) => (
                <div key={p.id} data-testid={`blog-admin-post-${p.id}`} className="flex items-center gap-3 border border-neutral-200 rounded-xl p-2">
                  {p.image_url && <img src={imgUrl(p.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-sm font-semibold text-neutral-800 hover:text-[#E8956B] line-clamp-1">{p.title}</a>
                    <p className="text-[11px] text-neutral-400">{p.date} · {p.keyword}</p>
                  </div>
                  <button data-testid={`delete-blog-${p.id}`} onClick={() => deleteBlogPost(p.id)} className="text-neutral-400 hover:text-red-500 shrink-0"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* OUTREACH EMAIL CAMPAIGN */}
      <h2 className="font-display text-xl font-bold mb-4 mt-12 flex items-center gap-2">
        <Mail size={20} className="text-[#E8956B]" /> Outreach Email
      </h2>
      <div data-testid="campaign-panel" className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-neutral-200 rounded-2xl p-6 space-y-5">
          <p className="text-xs text-neutral-500 -mt-1">
            Send a professional email to potential customers. Paste any email list — great for reaching
            people who aren't members yet.
          </p>
          <div data-testid="saved-campaigns-box" className="rounded-xl border border-[#E8956B]/30 bg-[#E8956B]/5 p-3">
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Saved campaigns</label>
            <div className="flex gap-2 mt-2">
              <Input
                data-testid="campaign-name-input"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="Name this campaign to save it"
                className="bg-white border-neutral-300 h-10"
              />
              <button data-testid="save-campaign-btn" onClick={saveCampaign}
                className="whitespace-nowrap bg-[#E8956B] hover:bg-[#CE744A] text-white text-sm font-bold rounded-full px-4 transition-all">
                {editingCampaignId ? "Update" : "Save"}
              </button>
              {editingCampaignId && (
                <button data-testid="new-campaign-btn" onClick={() => { setEditingCampaignId(null); setCampaignName(""); }}
                  className="whitespace-nowrap border border-neutral-300 text-neutral-500 text-sm font-semibold rounded-full px-3 hover:bg-neutral-100 transition-all">
                  New
                </button>
              )}
            </div>
            {savedCampaigns.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {savedCampaigns.map((c) => (
                  <span key={c.id} data-testid={`saved-campaign-${c.id}`}
                    className={`inline-flex items-center gap-1.5 rounded-full border pl-3 pr-1.5 py-1 text-xs font-semibold transition-all ${editingCampaignId === c.id ? "border-[#E8956B] bg-white text-[#CE744A]" : "border-neutral-300 bg-white text-neutral-600"}`}>
                    <button data-testid={`load-campaign-${c.id}`} onClick={() => loadCampaign(c)} className="hover:text-[#E8956B]">{c.name}</button>
                    <button data-testid={`delete-campaign-${c.id}`} onClick={() => deleteCampaign(c.id)} className="text-neutral-400 hover:text-red-500"><X size={13} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Template</label>
            <select
              data-testid="campaign-template-select"
              value={campaign.template}
              onChange={(e) => {
                const t = e.target.value;
                setCampaign((c) => ({
                  ...c,
                  template: t,
                  subject: t === "invite" ? "You're invited to Beauty Trade Club 💄" : "Welcome to Beauty Trade Club 💄",
                  cta_label: t === "invite" ? "Visit Beauty Trade Club" : "Explore the Club",
                  cta_url: t === "invite" ? "https://beautytradeclub.com/join?utm_source=email" : "https://beautytradeclub.com",
                }));
              }}
              className="mt-2 w-full bg-neutral-50 border border-neutral-300 rounded-xl px-3 h-11 outline-none focus:border-[#E8956B]"
            >
              <option value="welcome">Welcome / Intro (for members &amp; sign-ups)</option>
              <option value="invite">Invite — introduce the site to newcomers</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Subject line</label>
            <Input
              data-testid="campaign-subject-input"
              value={campaign.subject}
              onChange={(e) => setCampaign({ ...campaign, subject: e.target.value })}
              className="bg-neutral-50 border-neutral-300 mt-2 h-11"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Intro message (optional)</label>
            <Textarea
              data-testid="campaign-intro-input"
              value={campaign.intro_message}
              onChange={(e) => setCampaign({ ...campaign, intro_message: e.target.value })}
              className="bg-neutral-50 border-neutral-300 mt-2 min-h-24"
              placeholder="Leave blank to use our default welcome copy, or write your own hook..."
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Button link</label>
              <Input
                data-testid="campaign-cta-url-input"
                value={campaign.cta_url}
                onChange={(e) => setCampaign({ ...campaign, cta_url: e.target.value })}
                className="bg-neutral-50 border-neutral-300 mt-2 h-11"
                placeholder="https://beautytradeclub.com"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Button label</label>
              <Input
                data-testid="campaign-cta-label-input"
                value={campaign.cta_label}
                onChange={(e) => setCampaign({ ...campaign, cta_label: e.target.value })}
                className="bg-neutral-50 border-neutral-300 mt-2 h-11"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Recipients</label>
            <div className="flex flex-wrap gap-2 mt-2 mb-2">
              <button type="button" data-testid="add-all-leads-btn" onClick={() => addRecipientsFrom(leads, "leads")}
                className="text-xs font-bold border border-[#E8956B]/50 text-[#CE744A] hover:bg-[#E8956B]/10 rounded-full px-3 py-1.5 transition-all">
                + Add all leads ({leads.length})
              </button>
              <button type="button" data-testid="add-all-buyers-btn" onClick={() => addRecipientsFrom(buyers, "members")}
                className="text-xs font-bold border border-[#E8956B]/50 text-[#CE744A] hover:bg-[#E8956B]/10 rounded-full px-3 py-1.5 transition-all">
                + Add all members ({buyers.length})
              </button>
              <button type="button" data-testid="add-all-sellers-btn" onClick={() => addRecipientsFrom(sellers, "sellers")}
                className="text-xs font-bold border border-[#E8956B]/50 text-[#CE744A] hover:bg-[#E8956B]/10 rounded-full px-3 py-1.5 transition-all">
                + Add all sellers ({sellers.length})
              </button>
              {campaign.recipients && (
                <button type="button" data-testid="clear-recipients-btn" onClick={() => setCampaign((c) => ({ ...c, recipients: "" }))}
                  className="text-xs font-bold border border-neutral-300 text-neutral-500 hover:bg-neutral-100 rounded-full px-3 py-1.5 transition-all">
                  Clear
                </button>
              )}
            </div>
            <Textarea
              data-testid="campaign-recipients-input"
              value={campaign.recipients}
              onChange={(e) => setCampaign({ ...campaign, recipients: e.target.value })}
              className="bg-neutral-50 border-neutral-300 mt-2 min-h-24"
              placeholder={"One recipient per line. Examples:\nsarah@email.com\nSarah, sarah@email.com"}
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-neutral-400">{recipientLines().length} valid recipient(s) detected</p>
              <label data-testid="upload-recipients-label" className="text-xs font-bold text-[#E8956B] hover:text-[#CE744A] cursor-pointer flex items-center gap-1">
                <Download size={13} className="rotate-180" /> Upload CSV / TXT
                <input data-testid="upload-recipients-input" type="file" accept=".csv,.txt" className="hidden" onChange={uploadRecipients} />
              </label>
            </div>
            <p className="text-[11px] text-neutral-400 mt-1">Tip: add a name (<span className="font-mono">Sarah, sarah@email.com</span>) to personalize with "Hi Sarah 👋". Email-only lines get a friendly "Hi there 👋".</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button data-testid="send-test-email-btn" onClick={sendTestEmail} disabled={sendingTest}
              className="flex items-center gap-2 border-2 border-[#E8956B] text-[#E8956B] hover:bg-[#E8956B] hover:text-white font-bold rounded-full px-6 py-2.5 transition-all disabled:opacity-50">
              {sendingTest ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : <><Mail size={16} /> Send test to me</>}
            </button>
            <button data-testid="send-campaign-btn" onClick={sendCampaign} disabled={sendingCampaign}
              className="flex items-center gap-2 bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-6 py-2.5 transition-all disabled:opacity-50">
              {sendingCampaign ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : <><Send size={16} /> Send to list</>}
            </button>
          </div>
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-400 mb-2">Live preview</div>
          <div className="border border-neutral-200 rounded-2xl overflow-hidden bg-neutral-100">
            <iframe
              data-testid="campaign-preview-frame"
              title="Email preview"
              srcDoc={previewHtml}
              className="w-full"
              style={{ height: "620px", border: "none", background: "#f4f4f5" }}
            />
          </div>
        </div>
      </div>

      {/* LEADS */}
      <h2 className="font-display text-xl font-bold mb-4 mt-12 flex items-center gap-2">
        <UserPlus size={20} className="text-[#E8956B]" /> Leads
        <span className="text-sm font-normal text-neutral-400">({leads.length})</span>
      </h2>
      <div data-testid="leads-panel" className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
        {leads.length === 0 ? (
          <p className="p-6 text-sm text-neutral-500">No leads yet. Leads from the chat widget and the /join landing page will appear here.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Source</th>
                  <th className="text-left px-4 py-3">Captured</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-neutral-100">
                    <td className="px-4 py-3 font-semibold text-neutral-900">{l.email}</td>
                    <td className="px-4 py-3 text-neutral-600">{l.name || "—"}</td>
                    <td className="px-4 py-3"><span className="bg-[#fff1e8] text-[#CE744A] text-xs font-bold px-2 py-1 rounded-full capitalize">{l.source}</span></td>
                    <td className="px-4 py-3 text-neutral-500">{new Date(l.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* LINK-IN-BIO (tracked) */}
      <h2 className="font-display text-xl font-bold mb-2 mt-12 flex items-center gap-2">
        <ExternalLink size={20} className="text-[#E8956B]" /> Link-in-bio (tracked)
      </h2>
      <p className="text-sm text-neutral-500 mb-4">
        Paste these into each platform's bio/link. Every visit &amp; lead is auto-tagged by source, so you can see which
        platform performs best in the Leads table above.
      </p>
      <div data-testid="linkinbio-panel" className="bg-white border border-neutral-200 rounded-2xl p-5 grid sm:grid-cols-2 gap-3">
        {["tiktok", "instagram", "facebook", "x", "pinterest", "youtube"].map((plat) => {
          const url = `${window.location.origin}/join?utm_source=${plat}`;
          return (
            <div key={plat} className="flex items-center gap-2" data-testid={`linkinbio-${plat}`}>
              <span className="w-20 shrink-0 text-xs font-bold uppercase tracking-wider text-neutral-500 capitalize">{plat}</span>
              <input readOnly value={url} className="flex-1 min-w-0 bg-neutral-50 border border-neutral-300 rounded-lg px-3 py-2 text-xs text-neutral-700 outline-none" />
              <button data-testid={`copy-link-${plat}`} onClick={() => copyToClipboard(url, `${plat} link`)} className="text-neutral-400 hover:text-[#E8956B] shrink-0"><Copy size={15} /></button>
            </div>
          );
        })}
      </div>

      {/* SOCIAL STUDIO */}
      <h2 className="font-display text-xl font-bold mb-4 mt-12 flex items-center gap-2">
        <Wand2 size={20} className="text-[#E8956B]" /> Social Studio
      </h2>
      <div data-testid="social-panel" className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-neutral-200 rounded-2xl p-6 space-y-5">
          <p className="text-xs text-neutral-500 -mt-1">
            Generate ready-to-post captions, hashtags &amp; images for social. Post them yourself with your link to drive traffic.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Platform</label>
              <select data-testid="social-platform-select" value={social.platform} onChange={(e) => setSocial({ ...social, platform: e.target.value })}
                className="mt-2 w-full bg-neutral-50 border border-neutral-300 rounded-xl px-3 h-11 outline-none focus:border-[#E8956B]">
                <option value="instagram">Instagram</option>
                <option value="tiktok">TikTok</option>
                <option value="facebook">Facebook</option>
                <option value="x">X / Twitter</option>
                <option value="pinterest">Pinterest</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Tone</label>
              <select data-testid="social-tone-select" value={social.tone} onChange={(e) => setSocial({ ...social, tone: e.target.value })}
                className="mt-2 w-full bg-neutral-50 border border-neutral-300 rounded-xl px-3 h-11 outline-none focus:border-[#E8956B]">
                <option value="friendly">Friendly</option>
                <option value="playful">Playful</option>
                <option value="luxury">Luxury</option>
                <option value="bold">Bold</option>
                <option value="informative">Informative</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Topic / angle (optional)</label>
            <Input data-testid="social-topic-input" value={social.topic} onChange={(e) => setSocial({ ...social, topic: e.target.value })}
              className="bg-neutral-50 border-neutral-300 mt-2 h-11" placeholder="e.g. sell your unused makeup for cash" />
          </div>
          <label className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer">
            <input data-testid="social-image-toggle" type="checkbox" checked={social.with_image} onChange={(e) => setSocial({ ...social, with_image: e.target.checked })} className="accent-[#E8956B] w-4 h-4" />
            Also generate a matching image (AI)
          </label>
          <button data-testid="social-generate-btn" onClick={generateSocial} disabled={generating}
            className="flex items-center gap-2 bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-6 py-2.5 transition-all disabled:opacity-50">
            {generating ? <><Loader2 size={16} className="animate-spin" /> Generating...</> : <><Wand2 size={16} /> Generate post</>}
          </button>
        </div>

        <div className="bg-white border border-neutral-200 rounded-2xl p-6">
          {!genResult ? (
            <div className="h-full flex flex-col items-center justify-center text-neutral-400 py-10 text-center">
              <ImageIcon size={34} className="mb-3" />
              <p className="text-sm">Your generated post will appear here.</p>
            </div>
          ) : (
            <div data-testid="social-result" className="space-y-4">
              {genResult.image_url && (
                <div className="relative rounded-xl overflow-hidden border border-neutral-200">
                  <img src={imgUrl(genResult.image_url)} alt="Generated" className="w-full aspect-square object-cover" />
                  <a href={imgUrl(genResult.image_url)} download target="_blank" rel="noreferrer"
                    data-testid="social-download-image" className="absolute top-3 right-3 bg-white/90 backdrop-blur text-neutral-900 rounded-full p-2 shadow hover:bg-white">
                    <Download size={16} />
                  </a>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Caption</label>
                  <button data-testid="copy-caption-btn" onClick={() => copyToClipboard(genResult.caption, "Caption")} className="text-neutral-400 hover:text-[#E8956B]"><Copy size={15} /></button>
                </div>
                <p className="text-sm text-neutral-800 bg-neutral-50 rounded-xl p-3 whitespace-pre-wrap">{genResult.caption}</p>
              </div>
              {genResult.hashtags?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Hashtags</label>
                    <button data-testid="copy-hashtags-btn" onClick={() => copyToClipboard(genResult.hashtags.map((h) => `#${h}`).join(" "), "Hashtags")} className="text-neutral-400 hover:text-[#E8956B]"><Copy size={15} /></button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {genResult.hashtags.map((h) => (
                      <span key={h} className="text-xs bg-[#fff1e8] text-[#CE744A] font-semibold px-2 py-1 rounded-full">#{h}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
